import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  AlertCircle,
  Ban,
  Loader2,
  Plus,
  Search,
  ShieldCheck,
  Undo2,
  User as UserIcon,
} from 'lucide-react'
import Modal from '../components/ui/Modal'
import { api, apiErrorMessage } from '../lib/api'
import { promptDialog, toast } from '../lib/dialogs'
import { useAuth } from '../contexts/AuthContext'
import {
  MARKETING_SUPPRESSION_REASONS,
  type CreateMarketingSuppressionResponse,
  type MarketingSuppressionDto,
  type MarketingSuppressionListResponse,
  type MarketingSuppressionReason,
} from '@toga/shared'

/**
 * Lista de supresión — la primera pieza real del módulo de Marketing.
 *
 * Va antes que las campañas a propósito: montar el motor de envío primero y la
 * lista de bajas después es exactamente cómo se acaba escribiendo a quien ya
 * dijo que no.
 */

const PILL: Record<MarketingSuppressionReason, string> = {
  BAJA: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40',
  REBOTE: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  QUEJA: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40',
  MANUAL: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40',
}

/** Lo mismo que exige el backend: sacar a alguien de aquí se explica. */
const MOTIVO_MINIMO = 10

export default function MarketingSuppressions() {
  const { t, i18n } = useTranslation(['marketing', 'common'])
  const auth = useAuth()
  const role = auth.status === 'authenticated' ? auth.user.role : null
  const canWrite = role === 'ADMIN' || role === 'CONSULTOR' || role === 'ANALISTA'
  const canRemove = role === 'ADMIN' || role === 'CONSULTOR'

  const [items, setItems] = useState<MarketingSuppressionDto[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [reasonFilter, setReasonFilter] = useState<MarketingSuppressionReason | ''>('')
  const [adding, setAdding] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (reasonFilter) params.set('reason', reasonFilter)
      const r = await api.get<MarketingSuppressionListResponse>(
        `/marketing/suppressions?${params.toString()}`,
      )
      setItems(r.items)
      setTotal(r.total)
    } catch (err) {
      setError(apiErrorMessage(err, t('suppressions.errors.load')))
    } finally {
      setLoading(false)
    }
  }, [q, reasonFilter, t])

  useEffect(() => {
    void reload()
  }, [reload])

  const quitar = async (fila: MarketingSuppressionDto) => {
    const motivo = await promptDialog({
      title: t('suppressions.remove.title'),
      message: t('suppressions.remove.message', { email: fila.email }),
      placeholder: t('suppressions.remove.placeholder'),
      confirmLabel: t('suppressions.remove.confirm'),
    })
    if (motivo === null) return
    if (motivo.trim().length < MOTIVO_MINIMO) {
      toast.error(t('suppressions.remove.reasonTooShort'))
      return
    }
    try {
      await api.post(`/marketing/suppressions/${fila.id}/remove`, { reason: motivo.trim() })
      toast.success(t('suppressions.remove.done', { email: fila.email }))
      await reload()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('suppressions.errors.generic')))
    }
  }

  const fecha = (iso: string) => new Date(iso).toLocaleDateString(i18n.language)

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Ban size={20} className="text-fuchsia-600 dark:text-fuchsia-300" />
            {t('suppressions.title')}
          </h1>
          <p className="text-xs t-muted max-w-2xl">{t('suppressions.subtitle')}</p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            <Plus size={14} /> {t('suppressions.addButton')}
          </button>
        )}
      </header>

      {/* Qué significa esta pantalla, en una línea. No es decoración: quien
          entra aquí por primera vez tiene que saber qué bloquea y qué no. */}
      <div className="surface p-3 flex items-start gap-2 text-sm">
        <ShieldCheck size={16} className="text-emerald-500 shrink-0 mt-0.5" />
        <p className="t-secondary">{t('suppressions.explain')}</p>
      </div>

      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('suppressions.searchPlaceholder')}
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
          />
        </div>
        <label className="text-xs t-muted flex items-center gap-2">
          {t('suppressions.table.reason')}
          <select
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value as MarketingSuppressionReason | '')}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
          >
            <option value="">{t('suppressions.filters.allReasons')}</option>
            {MARKETING_SUPPRESSION_REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`suppressions.reasons.${r}`)}
              </option>
            ))}
          </select>
        </label>
        <span className="text-xs t-faint ml-auto">{t('suppressions.count', { n: total })}</span>
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
            <tr>
              <th className="px-3 py-2 text-left">{t('suppressions.table.email')}</th>
              <th className="px-3 py-2 text-left">{t('suppressions.table.reason')}</th>
              <th className="px-3 py-2 text-left">{t('suppressions.table.client')}</th>
              <th className="px-3 py-2 text-left">{t('suppressions.table.source')}</th>
              <th className="px-3 py-2 text-left">{t('suppressions.table.when')}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" />{' '}
                  {t('common:loading')}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center">
                  <Ban size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">
                    {q || reasonFilter
                      ? t('suppressions.empty.filtered')
                      : t('suppressions.empty.none')}
                  </p>
                </td>
              </tr>
            )}
            {items.map((s) => (
              <tr key={s.id} className="border-t border-base/40 hover:bg-hover">
                <td className="px-3 py-2 t-primary font-medium break-all">{s.email}</td>
                <td className="px-3 py-2">
                  <span
                    className={`px-1.5 py-0.5 rounded border text-xs whitespace-nowrap ${PILL[s.reason]}`}
                  >
                    {t(`suppressions.reasons.${s.reason}`)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {s.clientId ? (
                    <Link
                      to={`/clients/${s.clientId}`}
                      className="inline-flex items-center gap-1 text-toga-700 dark:text-toga-300 hover:underline"
                    >
                      <UserIcon size={12} /> {s.clientName}
                    </Link>
                  ) : (
                    <span className="t-faint">—</span>
                  )}
                </td>
                <td className="px-3 py-2 t-secondary">
                  {s.source || <span className="t-faint">—</span>}
                  {s.notes && <div className="t-faint text-xs mt-0.5">{s.notes}</div>}
                </td>
                <td className="px-3 py-2 t-secondary whitespace-nowrap">
                  {fecha(s.createdAt)}
                  {s.createdByName && <div className="t-faint text-xs">{s.createdByName}</div>}
                </td>
                <td className="px-3 py-2 text-right">
                  {/* Una QUEJA de spam no se quita ni siendo ADMIN: el backend
                      responde 409, y ofrecer el botón sería prometer algo que
                      no va a pasar. */}
                  {canRemove && s.reason !== 'QUEJA' && (
                    <button
                      type="button"
                      onClick={() => void quitar(s)}
                      title={t('suppressions.remove.title')}
                      className="p-1.5 rounded-lg t-muted hover:bg-hover hover:text-rose-600 dark:hover:text-rose-300"
                    >
                      <Undo2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adding && (
        <AddSuppressionsModal
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false)
            void reload()
          }}
        />
      )}
    </div>
  )
}

function AddSuppressionsModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation(['marketing', 'common'])
  const [emails, setEmails] = useState('')
  const [reason, setReason] = useState<MarketingSuppressionReason>('BAJA')
  const [source, setSource] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)

  const enviar = async () => {
    setBusy(true)
    try {
      const r = await api.post<CreateMarketingSuppressionResponse>('/marketing/suppressions', {
        emails,
        reason,
        source: source.trim() || null,
        notes: notes.trim() || null,
      })
      toast.success(t('suppressions.add.done', { n: r.creados, repetidos: r.yaEstaban }))
      if (r.invalidos.length > 0) {
        toast.error(t('suppressions.add.invalid', { list: r.invalidos.join(', ') }))
      }
      onDone()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('suppressions.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('suppressions.add.title')}
      description={t('suppressions.add.subtitle')}
      maxWidth="max-w-xl"
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="sup-emails" className="text-sm font-medium t-primary block">
            {t('suppressions.add.emailsLabel')}
          </label>
          <textarea
            id="sup-emails"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            rows={5}
            placeholder={t('suppressions.add.emailsPlaceholder')}
            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm font-mono focus:outline-none focus:border-toga-500/50"
          />
          <p className="text-xs t-faint">{t('suppressions.add.emailsHelp')}</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label htmlFor="sup-reason" className="text-sm font-medium t-primary block">
              {t('suppressions.table.reason')}
            </label>
            <select
              id="sup-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as MarketingSuppressionReason)}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-toga-500/50"
            >
              {MARKETING_SUPPRESSION_REASONS.map((r) => (
                <option key={r} value={r}>
                  {t(`suppressions.reasons.${r}`)}
                </option>
              ))}
            </select>
            <p className="text-xs t-faint">{t(`suppressions.reasonHelp.${reason}`)}</p>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="sup-source" className="text-sm font-medium t-primary block">
              {t('suppressions.table.source')}
            </label>
            <input
              id="sup-source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder={t('suppressions.add.sourcePlaceholder')}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-toga-500/50"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="sup-notes" className="text-sm font-medium t-primary block">
            {t('suppressions.add.notesLabel')}
          </label>
          <input
            id="sup-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-toga-500/50"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-sm t-secondary hover:bg-hover"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            disabled={busy || emails.trim().length < 3}
            onClick={() => void enviar()}
            className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
            {t('suppressions.add.submit')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
